import React, { useEffect } from 'react';
import { Nav } from './nav.tsx';
import { Footer } from './footer.tsx';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';

type ParlaFunctions = {
  load: () => void;
  [key: `popup${number}`]: () => void;
}

type ParlaDoc = Document & {
  parla: ParlaFunctions;
}

export function SellFromAnywhere() {
    const { lang } = useParams();
    const { t } = useTranslation();
    const baseURL = "https://www.parlaretail.com";
    useEffect(() => {
        if (lang) {
            i18next.changeLanguage(lang);
        }
    }, [lang]);

    return (
        <>
            <Helmet key={lang}>
                {/* General Meta */}
                <meta charSet="utf-8" />
                <meta http-equiv="X-UA-Compatible" content="IE=edge" />
                <title>{t('sell.meta.title')}</title>
                <meta name="description" content={t('sell.meta.description')} />
                <meta name="keywords" content={t('sell.meta.keywords')} />
                <meta name="viewport" content="width=device-width, initial-scale=1" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={t('sell.meta.title')} />
                <meta property="og:description" content={t('sell.meta.description')} />
                <meta property="og:url" content={`${baseURL}/${lang}/`} />
                <meta property="og:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={t('sell.meta.title')} />
                <meta name="twitter:description" content={t('sell.meta.description')} />
                <meta name="twitter:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Hreflang for Language Versioning */}
                <link rel="alternate" hrefLang="en" href={`${baseURL}/en/`} />
                <link rel="alternate" hrefLang="es" href={`${baseURL}/es/`} />

                {/* Favicon */}
                <link rel="icon" type="image/png" href={`${baseURL}/img/parla-fav.png`} />
            </Helmet>
            <Nav />
            <div className="page">
                {/* Hero Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="w50">
                                    <div className="title f60 mf40 t-color-2 mb20">{t('sell.hero.title')}</div>
                                    <div className="sub-title t-color-3 f20 mb20">
                                        {(t('sell.hero.subtitle', { returnObjects: true }) as string[]).map((text, index) => (
                                            <div key={index}>{text}</div>
                                        ))}
                                    </div>
                                    <div className="btn-wrap">
                                        <button className="btn btn-dark" onClick={() => (document as ParlaDoc).parla.popup33()}>{t('sell.hero.button')}</button>
                                    </div>
                                </div>
                                <div className="w50 v-center">
                                    <img src="/img/platform//Parla+Sell+from+anywhere.png" alt={t('sell.hero.image_alt')} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sell from Home Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row mp10">
                                <div className="w50">
                                    <div className="title f40 mf40 t-color-3 mb20">{t('sell.home.title')}</div>
                                    <div className="title f60 mf40 t-color-2 mb20">{t('sell.home.subtitle')}</div>
                                    <div className="sub-title t-color-3 f20 mb20">
                                        {t('sell.home.description')}
                                        <ul className="ml50 mt20">
                                            {(t('sell.home.features', { returnObjects: true }) as { bold: string; text: string }[]).map(
                                                (feature, index) => (
                                                    <li key={index}>
                                                        <span className="bold">{feature.bold}</span> {feature.text}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                </div>
                                <div className="w50 v-center row p20 ml20 mm0 mp0">
                                    <img
                                        className="rounded"
                                        src="/img/platform//Pic+of+young+girl+selling+with+phone+on+sofa.jpeg"
                                        alt={t('sell.home.image_alt')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sell from Stores Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="w50 v-center row p20 ml20 mm0 mp0">
                                    <img
                                        className="rounded"
                                        src="/img/platform//AdobeStock_322921886.jpeg"
                                        alt={t('sell.stores.image_alt')}
                                    />
                                </div>
                                <div className="w50">
                                    <div className="title f40 mf40 t-color-3 mb20">{t('sell.stores.title')}</div>
                                    <div className="title f60 mf40 t-color-2 mb20">{t('sell.stores.subtitle')}</div>
                                    <div className="sub-title t-color-3 f20 mb20">
                                        {t('sell.stores.description')}
                                        <ul className="ml50 mt20">
                                            {(t('sell.stores.features', { returnObjects: true }) as { bold: string; text: string }[]).map(
                                                (feature, index) => (
                                                    <li key={index}>
                                                        <span className="bold">{feature.bold}</span> {feature.text}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Call Centers Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="w50">
                                    <div className="title f40 mf40 t-color-3 mb20">{t('sell.call_centers.title')}</div>
                                    <div className="title f60 mf40 t-color-2 mb20">{t('sell.call_centers.subtitle')}</div>
                                    <div className="sub-title t-color-3 f20 mb20">
                                        {t('sell.call_centers.description')}
                                        <ul className="ml50 mt20">
                                            {(t('sell.call_centers.features', { returnObjects: true }) as { bold: string; text: string }[]).map(
                                                (feature, index) => (
                                                    <li key={index}>
                                                        <span className="bold">{feature.bold}</span> {feature.text}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                </div>
                                <div className="w50 v-center row p20 ml20 mm0 mp0">
                                    <img
                                        className="rounded"
                                        src="/img/platform//AdobeStock_500544777.jpeg"
                                        alt={t('sell.call_centers.image_alt')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Video Section */}
                <div className="section col mt50">
                    <div className="f70 mf40 t-color-3 t-center p20">{t('sell.video.title')}</div>
                    <div className="section-video mt100">
                        <div className="row">
                            <video muted autoPlay loop playsInline poster="/img/platform//parla-video1.jpg">
                                <source src="/img/parla_furniture_case.mp4 (720p).mp4" type="video/mp4" />
                                {t('sell.video.not_supported')}
                            </video>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}
